import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import btnClose from '../../images/common/close.svg';
import {
  Main,
  Header,
  Contents,
  CloseButton,
  CloseButtonImage,
} from '../../styles/common/Dialog';
import {
  Dialog,
  ButtonsWrapper,
  SearchBoxWrapper,
  CheckboxWrapper,
  Checkbox,
  DefaultCheckbox,
  CheckboxLabel,
  Image,
  ImageContainer,
  ImageWrapper,
  MediaContainer,
} from './styles/SelectMediaDialogStyles';
import AppButton from '../../styles/common/AppButton';
import ColorSF2 from '../../styles/common/ColorSF2';
import SearchBox from '../Parts/SearchBox';
import Pagination from '../Parts/Pagination';
import { SUCCESS_API_GET_SELECTABLE_REPORT_MEDIA } from '../../actions/actionTypes';
import useFetchMedia from './hooks/useFetchMedia';
import noImage from '../../images/media/work_record_no_images.png';
import createReportFrames from '../../actions/api/report/createReportFrames';
import convertTimelineToApiParams from './functions/convertTimelineToApiParams';
import getReportDetail from '../../actions/api/report/getReportDetail';
import {
  REPORT_UPDATE_ERROR_CODE,
  SELECT_MEDIA_DIALOG_MODE,
} from '../../lib/constants';
import updateReportFrame from '../../actions/api/report/updateReportFrame';
import checkReportStatus from './functions/checkReportStatus';
import useMediaSelection from './hooks/useMediaSelection';

const noImageId = 'noImage'; // 画像がない場合のID

/**
 * 写真選択ダイアログ。報告書フレームに追加する写真を選択する
 *
 * @param {object} props
 * @param {function} props.onClose - ダイアログを閉じる関数
 * @param {boolean} props.isGuest - ゲストユーザーかどうか
 * @param {string} props.selectMediaDialogMode - ダイアログのモード。'add'の場合、新規追加。'update'の場合、更新
 * @param {object} props.selectedUpdateImage - 更新モードの場合、選択された写真のID
 * @param {function} props.setSelectedUpdateImage - 更新モードの場合、選択された写真のIDを設定する関数
 * @param {object} props.selectedFrameInfo - 更新モードの場合、選択されたフレームの情報
 * @param {function} props.setConflictFrameId - 競合しているフレームのIDを設定する関数
 * @param {function} props.setConflictFrameImage - 競合しているフレームを上書きする画像を設定する関数
 * @param {object[]} props.frames - 現在設定されているフレーム情報
 * @returns
 */
const SelectMediaDialog = ({
  onClose,
  isGuest,
  selectMediaDialogMode,
  selectedUpdateImage,
  setSelectedUpdateImage,
  selectedFrameInfo,
  setConflictFrameId,
  setConflictFrameImage,
  frames,
}) => {
  const {
    timelines,
    totalNumber,
    totalPages,
    currentPage,
    setCurrentPage,
    isOnlyMyMediaChecked,
    handleSearchChange,
    handleCheckboxChange,
  } = useFetchMedia({
    delay: 500,
    successActionType: SUCCESS_API_GET_SELECTABLE_REPORT_MEDIA,
  });

  useEffect(() => {
    if (selectMediaDialogMode === SELECT_MEDIA_DIALOG_MODE.ADD) {
      setSelectedUpdateImage({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectMediaDialogMode]);

  const dispatch = useDispatch();
  const apiToken = useSelector(state => state.main.apiToken);
  const groupId = useSelector(state => state.main.targetGroupId);
  const reportId = useSelector(state => state.report.reportDetail.id);

  const { selectedAddImages, handleImageClick } = useMediaSelection({
    timelines,
    frames,
    setSelectedUpdateImage,
    selectMediaDialogMode,
    dispatch,
    noImageId,
  });

  // フレーム追加ボタンを押した時の処理
  const handleCreateFramesButtonClick = async () => {
    onClose();
    const apiParams = {
      frames: selectedAddImages.map(image => convertTimelineToApiParams(image)),
    };
    const result = await dispatch(
      createReportFrames(apiToken, groupId, reportId, apiParams),
    );
    // ステータスを完了に変更された場合の処理
    if (
      result?.response?.status === 400 &&
      result?.response?.data?.errorCode ===
        REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
    ) {
      await checkReportStatus(apiToken, groupId, reportId, dispatch);
    }
    dispatch(getReportDetail(apiToken, groupId, reportId));
  };

  // フレーム更新ボタンを押した時の処理
  const handleUpdateFramesButtonClick = async () => {
    onClose();
    const requestBody = convertTimelineToApiParams(selectedUpdateImage);
    const result = await dispatch(
      updateReportFrame(
        apiToken,
        groupId,
        reportId,
        selectedFrameInfo?.id,
        { ...requestBody, note: selectedFrameInfo?.note },
        selectedFrameInfo?.updatedAt,
      ),
    );
    // ステータスを完了に変更された場合の処理
    if (
      result?.response?.status === 400 &&
      result?.response?.data?.errorCode ===
        REPORT_UPDATE_ERROR_CODE.UNEDITABLE_CONTENT
    ) {
      await checkReportStatus(apiToken, groupId, reportId, dispatch);
    }
    // 競合エラーの場合の処理
    if (result?.response?.status === 412) {
      setConflictFrameId(selectedFrameInfo?.id);
      setConflictFrameImage(selectedUpdateImage);
    } else if (result?.status === 200) {
      dispatch(getReportDetail(apiToken, groupId, reportId));
    }
  };

  return (
    <Main>
      <Dialog>
        <Contents>
          <Header>
            <div>
              <FormattedMessage id="report.edit.selectMediaDialog.title" />
            </div>
            <CloseButton
              id="closeButton"
              role="presentation"
              className="button"
              onClick={() => onClose()}
            >
              <CloseButtonImage src={btnClose} alt="close" />
            </CloseButton>
          </Header>
          <SearchBoxWrapper>
            <SearchBox
              placeholder="タグ、メモ、撮影者、撮影日で検索"
              onChange={handleSearchChange}
              width="800px"
              bgColor={ColorSF2.whisperGray}
              height="40px"
            />
          </SearchBoxWrapper>
          <CheckboxWrapper>
            {!isGuest && (
              <Checkbox>
                <DefaultCheckbox
                  id="SetGroupCheckbox"
                  type="checkbox"
                  checked={isOnlyMyMediaChecked}
                  onChange={handleCheckboxChange}
                />
                <CheckboxLabel
                  className="checkBoxLabel"
                  htmlFor="SetGroupCheckbox"
                />
                <FormattedMessage id="report.edit.selectMediaDialog.checkbox.label" />
              </Checkbox>
            )}
          </CheckboxWrapper>
          <MediaContainer>
            <ImageWrapper>
              {currentPage === 1 && (
                <ImageContainer
                  onClick={() => handleImageClick(noImageId)}
                  isAddImageSelected={selectedAddImages.includes(noImageId)}
                  dataIndex={
                    selectedAddImages.includes(noImageId)
                      ? selectedAddImages.indexOf(noImageId) + 1
                      : undefined
                  }
                  isUpdateImageSelected={selectedUpdateImage === noImageId}
                >
                  <Image src={noImage} alt="noImage" />
                </ImageContainer>
              )}
              {timelines.map(item => {
                const isAddImageSelected = selectedAddImages.some(
                  selectedItem => selectedItem.id === item.id,
                );
                const dataIndex = isAddImageSelected
                  ? selectedAddImages.findIndex(
                      selectedItem => selectedItem.id === item.id,
                    ) + 1
                  : undefined;
                const isUpdateImageSelected =
                  selectedUpdateImage.id === item.id;

                return (
                  <ImageContainer
                    key={item.id}
                    onClick={() => handleImageClick(item.id)}
                    isAddImageSelected={isAddImageSelected}
                    isUpdateImageSelected={isUpdateImageSelected}
                    dataIndex={dataIndex}
                  >
                    <Image
                      key={item.id}
                      src={item.thumbnailUrl}
                      alt="thumbnail"
                    />
                  </ImageContainer>
                );
              })}
            </ImageWrapper>
          </MediaContainer>
          <Pagination
            totalNumber={totalNumber}
            totalPages={totalPages}
            currentPage={currentPage}
            onSelect={page => setCurrentPage(page)}
          />
          <ButtonsWrapper>
            <AppButton
              large
              bold
              bgColor={ColorSF2.white}
              color={ColorSF2.black}
              borderColor={ColorSF2.mediumGray}
              onClick={() => onClose()}
            >
              <FormattedMessage id="common.button.cancel" />
            </AppButton>
            {selectMediaDialogMode === SELECT_MEDIA_DIALOG_MODE.ADD && (
              <AppButton
                large
                bold
                bgColor={ColorSF2.navyBlue}
                disabled={selectedAddImages.length === 0}
                onClick={handleCreateFramesButtonClick}
              >
                <FormattedMessage id="common.button.add" />
              </AppButton>
            )}
            {selectMediaDialogMode === SELECT_MEDIA_DIALOG_MODE.UPDATE && (
              <AppButton
                large
                bold
                bgColor={ColorSF2.navyBlue}
                disabled={!selectedUpdateImage}
                onClick={handleUpdateFramesButtonClick}
              >
                <FormattedMessage id="common.button.update" />
              </AppButton>
            )}
          </ButtonsWrapper>
        </Contents>
      </Dialog>
    </Main>
  );
};

SelectMediaDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  selectMediaDialogMode: PropTypes.string.isRequired,
  selectedUpdateImage: PropTypes.object,
  setSelectedUpdateImage: PropTypes.func,
  selectedFrameInfo: PropTypes.object.isRequired,
  isGuest: PropTypes.bool.isRequired,
  setConflictFrameId: PropTypes.func.isRequired,
  setConflictFrameImage: PropTypes.func.isRequired,
  frames: PropTypes.array.isRequired,
};

SelectMediaDialog.defaultProps = {
  selectedUpdateImage: {},
  setSelectedUpdateImage: () => {},
};

export default SelectMediaDialog;
